import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine } from "@/components/site/Reveal";
import { IMAGES } from "@/data/content";
import { scrollToSection } from "@/hooks/useLenis";
import { ArrowDown } from "lucide-react";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-0 z-0"
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <img
          src={IMAGES.hero}
          alt="Renovated interior"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111110] via-[#111110]/50 to-[#111110]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111110]/85 via-[#111110]/10 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: overlayY }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-end pb-20"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-[#C05C3F]" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#C05C3F]">
            Est. 2018 — Design & Build
          </span>
        </motion.div>

        <h1 className="font-serif font-light tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-[#F4F3F0] max-w-4xl">
          <MaskLine delay={0.5}>We renovate the</MaskLine>
          <MaskLine delay={0.65} className="italic text-[#C05C3F]">
            houses you already
          </MaskLine>
          <MaskLine delay={0.8}>love to live in.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8"
        >
          <p className="max-w-md text-base sm:text-lg text-[#F4F3F0]/75 leading-relaxed font-light">
            A design-and-build studio for kitchens, bathrooms and whole-home
            transformations — obsessive about craft, honest about scope.
          </p>
          <button
            data-testid="hero-cta"
            onClick={() => scrollToSection("#quote")}
            className="group self-start sm:self-auto font-mono text-xs uppercase tracking-[0.2em] bg-[#C05C3F] text-[#F4F3F0] px-8 py-4 hover:bg-[#F4F3F0] hover:text-[#111110] transition-colors duration-300 flex items-center gap-3"
          >
            Start your project
            <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
